import { Check, Sparkles } from 'lucide-react';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ASSIGNABLE_ROLES } from '@/lib/can-manage-role';
import { useCustomRoles } from '@/lib/custom-roles';
import { hasPermission } from '@/lib/permissions';
import { PERMISSION_GROUPS, PERMISSION_LABELS, ROLE_COLORS, ROLE_LABELS } from '@/lib/role-display';
import { cn } from '@/lib/utils';

// Colonnes des rôles personnalisés : fond grisé + filet à gauche de la première, pour les séparer des rôles standards
const customCol = (i: number) => cn('text-center bg-muted/40', i === 0 && 'border-l-2 border-border');

/** Matrice pédagogique : ce que chaque rôle peut faire, permission par permission (lecture seule). */
export function PermissionsMatrix() {
  const query = useCustomRoles();
  const customRoles = query.data?.data ?? [];
  const columns = ASSIGNABLE_ROLES.length + customRoles.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2" aria-label="Légende des rôles">
        {ASSIGNABLE_ROLES.map((r) => <Badge key={r} className={ROLE_COLORS[r]}>{ROLE_LABELS[r]}</Badge>)}
      </div>
      <p className="text-sm text-muted-foreground">
        ✓ : le rôle dispose de la permission. Les droits sont fixés par rôle ; pour en changer, modifier le rôle du membre.
        {customRoles.length > 0 && ' Les rôles personnalisés (✨, fond grisé) remplacent les permissions du rôle standard de leurs membres.'}
      </p>
      <QueryError query={query} />
      <div className="bg-card rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Permission</TableHead>
              {ASSIGNABLE_ROLES.map((r) => <TableHead key={r} scope="col" className="text-center">{ROLE_LABELS[r]}</TableHead>)}
              {customRoles.map((r, i) => (
                <TableHead key={r.id} scope="col" data-custom-role={r.id} className={customCol(i)}>
                  <span className="inline-flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5 text-brand-violet" aria-hidden />
                    {r.name}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          {PERMISSION_GROUPS.map((g) => (
            <TableBody key={g.label}>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableCell colSpan={columns + 1} className="font-semibold">{g.label}</TableCell>
              </TableRow>
              {g.permissions.map((p) => (
                <TableRow key={p} data-permission={p}>
                  <TableCell>
                    {PERMISSION_LABELS[p]}
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{p}</span>
                  </TableCell>
                  {ASSIGNABLE_ROLES.map((r) => (
                    <TableCell key={r} data-role={r} className="text-center">
                      {hasPermission(r, p) && <Check className="inline h-4 w-4 text-success" aria-label="Oui" />}
                    </TableCell>
                  ))}
                  {customRoles.map((r, i) => (
                    <TableCell key={r.id} data-custom-role={r.id} className={customCol(i)}>
                      {r.permissions.includes(p) && <Check className="inline h-4 w-4 text-success" aria-label="Oui" />}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ))}
        </Table>
      </div>
    </div>
  );
}
